import { Button, Fab, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { MouseEvent, useCallback, useEffect, useState } from 'react'
import { Icons, SkeletonText } from 'utoniq-ui'

import {
  ConsoleEventListFragment,
  ConsoleEventsQuery,
  EventFilter,
  EventStatus,
  Maybe,
  MembershipSubscriptionProduct,
} from '@/clients/utoniq-core/schema'
import {
  ArtistHeaderItemsLeft,
  ArtistHeaderItemsLeftProps,
  Head,
} from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import {
  ConsoleEventItem,
  ConsoleEventItemSkeleton,
} from '@/components/organisms/console'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  artistId: string
  loading: boolean
  hasNextPage: boolean
  artist: ConsoleEventsQuery['artist']
  currentUser: Maybe<ArtistHeaderItemsLeftProps['currentUser']>
  changingEventStatus: boolean
  deletingEvent: boolean
  events: Maybe<{ edges: { node: ConsoleEventListFragment }[] }>
  filter: EventFilter
  membership: Pick<MembershipSubscriptionProduct, 'productId'>[] | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onFilter: (filterType: EventFilter) => Promise<void>
  onChangeEventStatus: (eventId: string, status: EventStatus) => Promise<void>
  onDeleteEvent: (eventId: string) => Promise<void>
}

export const ConsoleEventsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onFilter,
  onChangeEventStatus,
  onDeleteEvent,
}) => {
  const { t } = useTranslation('consoleEvents')
  const { routerPages, routerNames } = useRouter()
  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <ConsoleBaseLayout
        margin
        header={{
          left: (
            <ArtistHeaderItemsLeft
              currentUser={state.currentUser}
              paymentType={state.artist?.paymentType}
              hasArrangingDeliveryItems={
                state.artist?.hasArrangingDeliveryItems
              }
              membership={state.membership}
            />
          ),
          right: (
            <Button
              component="a"
              sx={{ minWidth: 124, mr: -3, color: 'white' }}
              variant="text"
              target="_blank"
              href={routerPages.ShokufesEvents}
            >
              <Icons.Window color="inherit" />
              <Typography sx={{ ml: 1 }} color="inherit" variant="subtitle1">
                {t('viewTheApp')}
              </Typography>
            </Button>
          ),
        }}
      >
        <Head title={routerNames.ConsoleShokufesEvents} />
        <HeadingCell
          loading={state.loading && !state.events}
          onFilter={onFilter}
          filter={state.filter}
        />
        <ScrollFetcher
          nodes={state.events?.edges.map(({ node }) => (
            <ConsoleEventItem
              event={node}
              key={node.id}
              artistId={state.artistId}
              changingEventStatus={state.changingEventStatus}
              deletingEvent={state.deletingEvent}
              onChangeEventStatus={status =>
                onChangeEventStatus(node.id, status)
              }
              onDeleteEvent={() => onDeleteEvent(node.id)}
            />
          ))}
          emptyMessage={t('eventEmptyMessage')}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          loadingComponent={
            <Stack spacing={5} sx={{ pt: 2 }}>
              {Array.from(Array(3).keys()).map(index => (
                <ConsoleEventItemSkeleton key={index} />
              ))}
            </Stack>
          }
        />
        <BaseCell />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type HeadingCellProps = {
  loading: boolean
  onFilter: Action['onFilter']
  filter: EventFilter
}
const HeadingCell: Cell<HeadingCellProps> = ({ loading, onFilter, filter }) => {
  const { t } = useTranslation('consoleEvents')
  const options = [
    EventFilter.All,
    EventFilter.Ongoing,
    EventFilter.Upcoming,
    EventFilter.Finished,
  ]
  // sort と options を比較して一致した値の添え字を返し、selectedIndex の初期値にする
  const initialSelectedIndex = filter
    ? options.findIndex(option => option === filter)
    : 0
  const [selectedIndex, setSelectedIndex] =
    useState<number>(initialSelectedIndex)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClickItem = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = (index: number) => {
    setSelectedIndex(index)
    onFilter(options[index])
    setAnchorEl(null)
  }

  useEffect(() => {
    const UrlParams = `?sort=${options[selectedIndex]}`
    // React Routerのhistoryを使うとソート順を変えた際に、スクロール位置が一番上になるため、JavaScriptで直接historyを利用している
    window.history.replaceState(null, '', UrlParams)
  }, [selectedIndex])

  if (loading) {
    return (
      <CellFrame sx={{ py: 3 }}>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
        >
          <SkeletonText variant="h3" width="40%" />
          <SkeletonText variant="h3" width="10%" />
        </Stack>
      </CellFrame>
    )
  }

  return (
    <CellFrame sx={{ py: 3 }}>
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Typography variant="h3">{t('title')}</Typography>
        <Button
          variant="text"
          onClick={handleClickItem}
          sx={{ mr: -1 }}
          endIcon={<Icons.DownArrow fontSize="xSmall" />}
        >
          <Typography variant="caption">{t(options[selectedIndex])}</Typography>
        </Button>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          {options.map((option, index) => (
            <MenuItem
              key={index}
              selected={index === selectedIndex}
              sx={{ minWidth: 200 }}
              onClick={() => handleMenuItemClick(index)}
            >
              <Typography variant="body2">{t(option)}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </Stack>
    </CellFrame>
  )
}

const BaseCell: Cell = () => {
  const { router, routerPages } = useRouter()
  return (
    <Stack direction="row-reverse">
      <Stack
        sx={{
          position: 'fixed',
          bottom: 16,
          zIndex: 5,
          maxWidth: 600,
          margin: '0 auto',
          px: 4,
          pb: styles.safeAreaBottom,
        }}
      >
        <Fab
          size="medium"
          color="primary"
          onClick={() => router.push(routerPages.ConsoleShokufesEventCreate)}
        >
          <Icons.ThinPlus fontSize="medium" sx={{ color: 'white' }} />
        </Fab>
      </Stack>
    </Stack>
  )
}
